// contexts/PayrollContext.tsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
// import { useAuth } from "./AuthContext";
import {
  RUN_PAYROLL,
  PROCESS_PAYROLL,
  LIST_PAYROLL_RUNS,
  GET_PAYROLL_RUN,
  GET_MY_PAY_STUBS,
  GET_EMPLOYEE_PAY_STUB,
  LIST_EMPLOYEE_PAYROLL_INFO,
  CREATE_EMPLOYEE_PAYROLL_INFO,
  UPDATE_EMPLOYEE_PAYROLL_INFO,
} from "../graphql/timesheet";

interface PayrollContextType {
  // State
  payrollRuns: any[];
  currentPayroll: any;
  payStubs: any[];
  employeePayrollInfo: any[];
  loading: boolean;

  // Actions
  runPayroll: (payPeriodStart: string, payPeriodEnd: string) => Promise<any>;
  processPayroll: (payrollRunId: string) => Promise<any>;
  loadPayrollRuns: (filters?: any) => void;
  loadPayrollRun: (id: string) => void;
  loadMyPayStubs: () => void;
  loadEmployeePayStub: (
    employeeId: string,
    payrollRunId: string
  ) => Promise<any>;
  loadEmployeePayrollInfo: () => void;
  createEmployeePayrollInfo: (employeeId: string, input: any) => Promise<any>;
  updateEmployeePayrollInfo: (employeeId: string, input: any) => Promise<any>;

  // Utilities
  calculateTaxes: (
    grossPay: number,
    taxWithholdings: any,
    ytdEarnings: number
  ) => any;
}

const PayrollContext = createContext<PayrollContextType | undefined>(undefined);

export const usePayroll = () => {
  const context = useContext(PayrollContext);
  if (!context) {
    throw new Error("usePayroll must be used within a PayrollProvider");
  }
  return context;
};

export const PayrollProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // const { user } = useAuth();
  const [payrollRuns, setPayrollRuns] = useState<any[]>([]);
  const [currentPayroll, setCurrentPayroll] = useState<any>(null);
  const [payStubs, setPayStubs] = useState<any[]>([]);
  const [employeePayrollInfo, setEmployeePayrollInfo] = useState<any[]>([]);

  // GraphQL Operations
  const [runPayrollMutation] = useMutation(RUN_PAYROLL, {
    onCompleted: (data) => {
      setCurrentPayroll(data.runPayroll);
      // Refresh payroll runs list
      loadPayrollRuns();
    },
  });

  const [processPayrollMutation] = useMutation(PROCESS_PAYROLL, {
    onCompleted: (data) => {
      setCurrentPayroll((prev: any) => ({
        ...prev,
        status: data.processPayroll.status,
      }));
      loadPayrollRuns();
    },
  });

  const [loadPayrollRunsQuery, { loading: payrollRunsLoading }] = useLazyQuery(
    LIST_PAYROLL_RUNS,
    {
      onCompleted: (data) => {
        setPayrollRuns(data.listPayrollRuns.items);
      },
    }
  );

  const [loadPayrollRunQuery, { loading: payrollRunLoading }] = useLazyQuery(
    GET_PAYROLL_RUN,
    {
      onCompleted: (data) => {
        setCurrentPayroll(data.getPayrollRun);
      },
    }
  );

  const [loadMyPayStubsQuery, { loading: payStubsLoading }] = useLazyQuery(
    GET_MY_PAY_STUBS,
    {
      onCompleted: (data) => {
        setPayStubs(data.getMyPayStubs.items);
      },
    }
  );

  const [loadEmployeePayStubQuery] = useLazyQuery(GET_EMPLOYEE_PAY_STUB);

  const [loadEmployeePayrollInfoQuery, { loading: payrollInfoLoading }] =
    useLazyQuery(LIST_EMPLOYEE_PAYROLL_INFO, {
      onCompleted: (data) => {
        setEmployeePayrollInfo(data.listEmployeePayrollInfo.items);
      },
    });

  const [createEmployeePayrollInfoMutation] = useMutation(
    CREATE_EMPLOYEE_PAYROLL_INFO
  );
  const [updateEmployeePayrollInfoMutation] = useMutation(
    UPDATE_EMPLOYEE_PAYROLL_INFO
  );

  // Tax Calculation (Keep your existing logic)
  const calculateTaxes = useCallback(
    (grossPay: number, taxWithholdings: any) => {
      // (grossPay: number, taxWithholdings: any, ytdEarnings: number) => {
      // Your existing tax calculation logic from the previous implementation
      const federal = grossPay * (taxWithholdings?.federal || 0.12);
      const state = grossPay * 0.05;
      const socialSecurity = taxWithholdings?.socialSecurity
        ? grossPay * 0.062
        : 0;
      const medicare = taxWithholdings?.medicare ? grossPay * 0.0145 : 0;

      return {
        federal,
        state,
        socialSecurity,
        medicare,
        total: federal + state + socialSecurity + medicare,
      };
    },
    []
  );

  // Action Functions
  const runPayroll = useCallback(
    async (payPeriodStart: string, payPeriodEnd: string) => {
      try {
        const result = await runPayrollMutation({
          variables: { payPeriodStart, payPeriodEnd },
        });
        return result.data.runPayroll;
      } catch (error) {
        console.error("Error running payroll:", error);
        throw error;
      }
    },
    [runPayrollMutation]
  );

  const processPayroll = useCallback(
    async (payrollRunId: string) => {
      try {
        const result = await processPayrollMutation({
          variables: { payrollRunId },
        });
        return result.data.processPayroll;
      } catch (error) {
        console.error("Error processing payroll:", error);
        throw error;
      }
    },
    [processPayrollMutation]
  );

  const loadPayrollRuns = useCallback(
    (filters?: any) => {
      loadPayrollRunsQuery({ variables: filters });
    },
    [loadPayrollRunsQuery]
  );

  const loadPayrollRun = useCallback(
    (id: string) => {
      loadPayrollRunQuery({ variables: { id } });
    },
    [loadPayrollRunQuery]
  );

  const loadMyPayStubs = useCallback(() => {
    loadMyPayStubsQuery();
  }, [loadMyPayStubsQuery]);

  const loadEmployeePayStub = useCallback(
    async (employeeId: string, payrollRunId: string) => {
      try {
        const result = await loadEmployeePayStubQuery({
          variables: { employeeId, payrollRunId },
        });
        return result.data?.getEmployeePayStub;
      } catch (error) {
        console.error("Error loading employee pay stub:", error);
        throw error;
      }
    },
    [loadEmployeePayStubQuery]
  );

  const loadEmployeePayrollInfo = useCallback(() => {
    loadEmployeePayrollInfoQuery();
  }, [loadEmployeePayrollInfoQuery]);

  const createEmployeePayrollInfo = useCallback(
    async (employeeId: string, input: any) => {
      try {
        const result = await createEmployeePayrollInfoMutation({
          variables: { employeeId, input },
        });
        return result.data.createEmployeePayrollInfo;
      } catch (error) {
        console.error("Error creating employee payroll info:", error);
        throw error;
      }
    },
    [createEmployeePayrollInfoMutation]
  );

  const updateEmployeePayrollInfo = useCallback(
    async (employeeId: string, input: any) => {
      try {
        const result = await updateEmployeePayrollInfoMutation({
          variables: { employeeId, input },
        });
        return result.data.updateEmployeePayrollInfo;
      } catch (error) {
        console.error("Error updating employee payroll info:", error);
        throw error;
      }
    },
    [updateEmployeePayrollInfoMutation]
  );

  const loading =
    payrollRunsLoading ||
    payrollRunLoading ||
    payStubsLoading ||
    payrollInfoLoading;

  const value = {
    payrollRuns,
    currentPayroll,
    payStubs,
    employeePayrollInfo,
    loading,
    runPayroll,
    processPayroll,
    loadPayrollRuns,
    loadPayrollRun,
    loadMyPayStubs,
    loadEmployeePayStub,
    loadEmployeePayrollInfo,
    createEmployeePayrollInfo,
    updateEmployeePayrollInfo,
    calculateTaxes,
  };

  return (
    <PayrollContext.Provider value={value}>{children}</PayrollContext.Provider>
  );
};
